import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import { UserProfile, Workout, WorkoutHistory, DailyReadiness, TrainingProgram } from '../types';

interface AppState {
  userProfile: UserProfile | null;
  currentWorkout: Workout | null;
  workoutHistory: WorkoutHistory[];
  isLoading: boolean;
  dailyReadiness: DailyReadiness | null;
  programs: TrainingProgram[];
  activeProgramId: string | null;
  plannerHistory: TrainingProgram[];
}

const HISTORY_STORAGE_KEY = 'treinopro_history_v1';
const READINESS_STORAGE_KEY = 'treinopro_readiness_v1';
const PROGRAMS_STORAGE_KEY = 'treinopro_programs_v1';
const ACTIVE_PROGRAM_STORAGE_KEY = 'treinopro_active_program_v1';
const PLANNER_HISTORY_STORAGE_KEY = 'treinopro_planner_history_v1';

type AppAction =
  | { type: 'SET_USER_PROFILE'; payload: UserProfile }
  | { type: 'SET_CURRENT_WORKOUT'; payload: Workout }
  | { type: 'ADD_WORKOUT_HISTORY'; payload: WorkoutHistory }
  | { type: 'UPDATE_WORKOUT_HISTORY'; payload: { index: number; history: WorkoutHistory } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'COMPLETE_WORKOUT'; payload: { workoutId: string } }
  | { type: 'HYDRATE_HISTORY'; payload: WorkoutHistory[] }
  | { type: 'SET_READINESS'; payload: DailyReadiness | null }
  | { type: 'SET_PROGRAMS'; payload: TrainingProgram[] }
  | { type: 'SET_ACTIVE_PROGRAM'; payload: string | null }
  | { type: 'UPSERT_PROGRAM'; payload: TrainingProgram }
  | { type: 'SET_PLANNER_HISTORY'; payload: TrainingProgram[] };

const initialState: AppState = {
  userProfile: null,
  currentWorkout: null,
  workoutHistory: [],
  isLoading: false,
  dailyReadiness: null,
  programs: [],
  activeProgramId: null,
  plannerHistory: [],
};

interface AppContextValue {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  startWorkout: (workout: Workout) => void;
  completeWorkout: (workoutId: string) => void;
  userProfile: UserProfile | null;
  currentWorkout: Workout | null;
  workoutHistory: WorkoutHistory[];
  isLoading: boolean;
  dailyReadiness: DailyReadiness | null;
  setDailyReadiness: (data: DailyReadiness | null) => void;
  programs: TrainingProgram[];
  activeProgram: TrainingProgram | null;
  setPrograms: (programs: TrainingProgram[]) => void;
  setActiveProgram: (programId: string | null) => void;
  upsertProgram: (program: TrainingProgram) => void;
  plannerHistory: TrainingProgram[];
  setPlannerHistory: (history: TrainingProgram[]) => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_USER_PROFILE':
      return { ...state, userProfile: action.payload };
    case 'SET_CURRENT_WORKOUT':
      return { ...state, currentWorkout: action.payload };
        case 'COMPLETE_WORKOUT': {
          const workoutToComplete = state.currentWorkout;
          if (!workoutToComplete || workoutToComplete.id !== action.payload.workoutId) {
            return state;
          }
          
          let totalVolume = 0;
          workoutToComplete.exercises.forEach(ex => {
            if (ex.setRecords) {
              ex.setRecords.forEach(record => {
                if (record.weight && record.reps) {
                  totalVolume += record.weight * record.reps;
                }
              });
            } else if (ex.weight) {
              const reps = parseInt(ex.reps.split('-')[0] || '0', 10);
              totalVolume += ex.weight * reps * ex.sets;
            }
          });
          
          const completedWorkout: Workout = {
            ...workoutToComplete,
            completed: true,
            date: new Date(),
          };
          const newHistoryItem: WorkoutHistory = {
            workout: completedWorkout,
            date: completedWorkout.date,
            totalVolume,
            duration: completedWorkout.duration,
          };
          return {
            ...state,
            currentWorkout: null,
            workoutHistory: [...state.workoutHistory, newHistoryItem],
          };
        }
    case 'ADD_WORKOUT_HISTORY':
      return { ...state, workoutHistory: [...state.workoutHistory, action.payload] };
    case 'UPDATE_WORKOUT_HISTORY': {
      const updated = [...state.workoutHistory];
      updated[action.payload.index] = action.payload.history;
      return { ...state, workoutHistory: updated };
    }
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'HYDRATE_HISTORY':
      if (!action.payload || action.payload.length === 0) {
        return state;
      }
      return { ...state, workoutHistory: action.payload };
    case 'SET_READINESS':
      return { ...state, dailyReadiness: action.payload };
    case 'SET_PROGRAMS':
      return { ...state, programs: action.payload };
    case 'SET_ACTIVE_PROGRAM':
      return { ...state, activeProgramId: action.payload };
    case 'UPSERT_PROGRAM': {
      const programs = state.programs;
      const index = programs.findIndex((program) => program.id === action.payload.id);
      if (index === -1) {
        return { ...state, programs: [...programs, action.payload] };
      }
      const updated = [...programs];
      updated[index] = action.payload;
      return { ...state, programs: updated };
    }
    case 'SET_PLANNER_HISTORY':
      return { ...state, plannerHistory: action.payload };
    default:
      return state;
  }
}

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return;
      const hydrated: WorkoutHistory[] = parsed.map((item) => ({
        ...item,
        date: item.date ? new Date(item.date) : new Date(),
        workout: {
          ...item.workout,
          date: item.workout?.date ? new Date(item.workout.date) : new Date(),
        },
      }));
      if (hydrated.length > 0) {
        dispatch({ type: 'HYDRATE_HISTORY', payload: hydrated });
      }
    } catch {
      // ignore malformed storage
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const serialized = JSON.stringify(
      state.workoutHistory.map((item) => ({
        ...item,
        date: item.date?.toISOString(),
        workout: {
          ...item.workout,
          date: item.workout.date?.toISOString(),
        },
      }))
    );
    localStorage.setItem(HISTORY_STORAGE_KEY, serialized);
  }, [state.workoutHistory]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = localStorage.getItem(READINESS_STORAGE_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (parsed && parsed.date) {
        dispatch({ type: 'SET_READINESS', payload: parsed });
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!state.dailyReadiness) {
      localStorage.removeItem(READINESS_STORAGE_KEY);
      return;
    }
    localStorage.setItem(READINESS_STORAGE_KEY, JSON.stringify(state.dailyReadiness));
  }, [state.dailyReadiness]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedPrograms = localStorage.getItem(PROGRAMS_STORAGE_KEY);
    if (storedPrograms) {
      try {
        const parsed = JSON.parse(storedPrograms) as TrainingProgram[];
        if (Array.isArray(parsed)) {
          dispatch({ type: 'SET_PROGRAMS', payload: parsed });
        }
      } catch {
        // ignore
      }
    }
    const storedActiveProgram = localStorage.getItem(ACTIVE_PROGRAM_STORAGE_KEY);
    if (storedActiveProgram) {
      dispatch({ type: 'SET_ACTIVE_PROGRAM', payload: storedActiveProgram });
    }
    const storedPlannerHistory = localStorage.getItem(PLANNER_HISTORY_STORAGE_KEY);
    if (storedPlannerHistory) {
      try {
        const parsedHistory = JSON.parse(storedPlannerHistory) as TrainingProgram[];
        if (Array.isArray(parsedHistory)) {
          dispatch({ type: 'SET_PLANNER_HISTORY', payload: parsedHistory });
        }
      } catch {
        // ignore history errors
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(PROGRAMS_STORAGE_KEY, JSON.stringify(state.programs));
  }, [state.programs]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!state.activeProgramId) {
      localStorage.removeItem(ACTIVE_PROGRAM_STORAGE_KEY);
      return;
    }
    localStorage.setItem(ACTIVE_PROGRAM_STORAGE_KEY, state.activeProgramId);
  }, [state.activeProgramId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(PLANNER_HISTORY_STORAGE_KEY, JSON.stringify(state.plannerHistory));
  }, [state.plannerHistory]);

  const startWorkout = (workout: Workout) => {
    dispatch({ type: 'SET_CURRENT_WORKOUT', payload: workout });
  };

  const completeWorkout = (workoutId: string) => {
    dispatch({ type: 'COMPLETE_WORKOUT', payload: { workoutId } });
  };

  const setDailyReadiness = (data: DailyReadiness | null) => {
    dispatch({ type: 'SET_READINESS', payload: data });
  };

  const setPrograms = (programs: TrainingProgram[]) => {
    dispatch({ type: 'SET_PROGRAMS', payload: programs });
  };

  const setActiveProgram = (programId: string | null) => {
    dispatch({ type: 'SET_ACTIVE_PROGRAM', payload: programId });
  };

  const upsertProgram = (program: TrainingProgram) => {
    dispatch({ type: 'UPSERT_PROGRAM', payload: program });
  };

  const activeProgram = state.programs.find((program) => program.id === state.activeProgramId) || null;

  const setPlannerHistory = (history: TrainingProgram[]) => {
    dispatch({ type: 'SET_PLANNER_HISTORY', payload: history });
  };

  return (
    <AppContext.Provider
      value={{
        state,
        dispatch,
        startWorkout,
        completeWorkout,
        userProfile: state.userProfile,
        currentWorkout: state.currentWorkout,
        workoutHistory: state.workoutHistory,
        isLoading: state.isLoading,
        dailyReadiness: state.dailyReadiness,
        setDailyReadiness,
        programs: state.programs,
        activeProgram,
        setPrograms,
        setActiveProgram,
        upsertProgram,
        plannerHistory: state.plannerHistory,
        setPlannerHistory,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
